import React, { useState, useEffect } from "react";
import { useThread } from "../context/ThreadContext";
import { ThreadService } from "../services/ThreadService";
import { db } from "../firebase.config";
import { collection, query, where, getDocs } from "firebase/firestore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Users, Building2, Crown, Info } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

const AssistantsDialog = ({ isOpen, onClose, currentUser }) => {
  const { dispatch } = useThread();
  const [orgAssistants, setOrgAssistants] = useState([]);
  const [loading, setLoading] = useState(true);

  const defaultAssistants = [
    {
      name: "HR Assistant",
      description:
        "Your dedicated helper for HR policies, employee relations, and workplace matters",
      userPrompt:
        "Hello! I am your HR assistant. How can I help you with HR-related matters today?",
      systemPrompt:
        "You are an HR assistant who helps with company policies, employee issues, and workplace regulations.",
      category: "hr",
    },
    {
      name: "Company Info Assistant",
      description:
        "Your guide to company policies, procedures, and organizational information",
      userPrompt:
        "Hi! I can help you with company-related information. What would you like to know?",
      systemPrompt:
        "You are a company information assistant who helps employees find information about company policies, procedures, and organizational details.",
      category: "company",
    },
    {
      name: "CEO's Assistant",
      description:
        "Your connection to executive-level insights and strategic business guidance",
      userPrompt:
        "Welcome! I am the CEO assistant. How may I assist you with executive matters?",
      systemPrompt:
        "You are the CEO's assistant. You help executives with strategic business decisions, leadership insights, and high-level company direction.",
      category: "executive",
    },
  ];

  useEffect(() => {
    if (isOpen && currentUser?.org_id) {
      loadOrgAssistants(currentUser.org_id);
    } else if (isOpen) {
      setOrgAssistants([]);
      setLoading(false);
    }
  }, [isOpen, currentUser]);

  const loadOrgAssistants = async (orgId) => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "prompts"),
        where("org_id", "==", orgId),
        where("isActive", "==", true),
        where("type", "==", "assistant")
      );

      const querySnapshot = await getDocs(q);
      const assistants = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setOrgAssistants(assistants);
    } catch (error) {
      console.error("Error loading organization assistants:", error);
      setOrgAssistants([]);
    } finally {
      setLoading(false);
    }
  };

  const getAssistantIcon = (category) => {
    switch (category) {
      case "hr":
        return <Users className="w-8 h-8" />;
      case "company":
        return <Building2 className="w-8 h-8" />;
      case "executive":
        return <Crown className="w-8 h-8" />;
      default:
        return <Info className="w-8 h-8" />;
    }
  };

  const handleAssistantSelect = async (assistant) => {
    try {
      const threadId = await ThreadService.createThreadWithSystem(
        currentUser.uid,
        assistant.name,
        assistant.systemPrompt,
        assistant.userPrompt
      );
      const newThread = await ThreadService.getUserThreads(currentUser.uid);
      dispatch({ type: "SET_THREADS", payload: newThread });
      dispatch({ type: "SET_CURRENT_THREAD", payload: newThread[0] });
      onClose();
    } catch (error) {
      console.error("Error creating assistant thread:", error);
    }
  };

  const assistants =
    orgAssistants.length > 0 ? orgAssistants : defaultAssistants;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-2xl font-bold tracking-tight">
            Choose Your Assistant
          </DialogTitle>
        </DialogHeader>
        <div className="p-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col space-y-3">
                  <Skeleton className="h-[125px] rounded-lg" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[150px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assistants.map((assistant, index) => (
                <button
                  key={index}
                  onClick={() => handleAssistantSelect(assistant)}
                  className="group relative flex flex-col items-center text-left p-6 rounded-lg border border-border bg-card transition-all duration-200 hover:border-primary hover:shadow-lg"
                >
                  <div className="mb-4 p-2 rounded-full bg-primary/10 text-primary transition-colors duration-200 group-hover:bg-primary group-hover:text-primary-foreground">
                    {getAssistantIcon(assistant.category)}
                  </div>
                  <h3 className="text-lg w-full text-center font-semibold mb-2">
                    {assistant.name}
                  </h3>
                  <p className="text-sm w-full text-center text-muted-foreground">
                    {assistant.description}
                  </p>
                  <div className="absolute inset-0 rounded-lg ring-2 ring-transparent transition-all duration-200 group-hover:ring-primary/20" />
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssistantsDialog;
